import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Apariencia } from '@alexiarest/shared';

/**
 * Tema claro / oscuro (tokens v3, 14 oct 2026).
 *
 * «Apariencia» tiene tres valores: `auto` (sigue al sistema, por defecto),
 * `claro` y `oscuro`. Se aplica en <html> como `data-tema="auto|claro|oscuro"`
 * —lo que pide el archivo de tokens— y, además, `data-tema-actual` con el tema
 * que se ve de verdad, para los retoques de componentes en oscuro.
 *
 * Se guarda POR USUARIO en su perfil (API). En el navegador solo queda una
 * copia para pintar bien el primer fotograma, antes de que llegue /me.
 */

export type Tema = 'claro' | 'oscuro';
export type PreferenciaTema = Apariencia;

const STORAGE_KEY = 'alexiarest.tema';

interface ThemeValue {
  /** Lo que eligió el usuario: auto, claro u oscuro */
  preferencia: PreferenciaTema;
  /** El tema que se está aplicando de verdad */
  tema: Tema;
  setPreferencia: (value: PreferenciaTema) => void;
  alternar: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

export function normalizarApariencia(valor: unknown): PreferenciaTema {
  return valor === 'claro' || valor === 'oscuro' ? valor : 'auto';
}

function leerPreferencia(): PreferenciaTema {
  if (typeof window === 'undefined') return 'auto';
  try {
    return normalizarApariencia(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'auto';
  }
}

function temaDelSistema(): Tema {
  if (typeof window === 'undefined' || !window.matchMedia) return 'claro';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oscuro' : 'claro';
}

export function ThemeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [preferencia, setPreferenciaState] = useState<PreferenciaTema>(leerPreferencia);
  const [sistema, setSistema] = useState<Tema>(temaDelSistema);

  // En automático hay que reaccionar cuando cambia el sistema (al anochecer en macOS).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSistema(event.matches ? 'oscuro' : 'claro');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const tema: Tema = preferencia === 'auto' ? sistema : preferencia;

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-tema', preferencia);
    html.setAttribute('data-tema-actual', tema);
  }, [preferencia, tema]);

  const setPreferencia = useCallback((value: PreferenciaTema) => {
    setPreferenciaState(value);
    try {
      if (value === 'auto') window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* sin almacenamiento: vale lo del perfil */
    }
  }, []);

  const alternar = useCallback(() => {
    setPreferencia(tema === 'oscuro' ? 'claro' : 'oscuro');
  }, [tema, setPreferencia]);

  const value = useMemo<ThemeValue>(
    () => ({ preferencia, tema, setPreferencia, alternar }),
    [preferencia, tema, setPreferencia, alternar],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme se tiene que usar dentro de <ThemeProvider>');
  return value;
}

/**
 * Aplica la apariencia guardada en el perfil del usuario en cuanto llega /me.
 * Solo cuando el perfil la trae: las cuentas viejas siguen con lo del navegador.
 */
export function useAparienciaDelPerfil(guardada: Apariencia | undefined): void {
  const { preferencia, setPreferencia } = useTheme();
  useEffect(() => {
    if (guardada && guardada !== preferencia) setPreferencia(normalizarApariencia(guardada));
    // Solo cuando cambia lo guardado: si no, pisaría la elección recién hecha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardada]);
}
